/**
 * Team matching + player following for a Build Itinerary tournament.
 *
 * This module is the shared home for the team panel used across the wizard's
 * Match Teams (step 2) and Follow Players (step 3) steps and the standalone
 * event view:
 * - `TeamAssociationPanel` — the panel itself, with a `mode` (`match` | `follow`
 *   | `both`) selecting which controls each row exposes.
 * - `ParseSummary` — a "what we parsed" breakdown (games / teams / days /
 *   fields), shown by the Match Teams step before matching.
 *
 * Assigning a team re-points every game naming it (via the team-associations
 * endpoint), so that team's roster / starred prospects flow into the itinerary's
 * matched players. Confident name matches are already linked at import; matching
 * fixes the rest, and following picks who to track.
 */

import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ProspectResponse, TeamResponse } from "../../../../../types/api";
import type { TournamentGameDto } from "../../../../../types/tournament-games";
import {
  getTeams,
  getFollowedTeams,
  followTeam,
  unfollowTeam,
  getFollowedTeamNames,
  followTeamByName,
  unfollowTeamByName,
} from "../../../../../services/teams";
import {
  getAllProspectsForTeam,
  toggleFavorite,
} from "../../../../../services/prospects";
import { associateTournamentTeam } from "../../../../../services/tournament-games";
import {
  distinctTournamentTeams,
  formatDayHeading,
  normalizeTeamName,
  playerInitials,
  summarizeParse,
  type TournamentTeamRef,
} from "./itinerary";
import { tourAnchor, type TourAnchorId } from "../../../../tours/tourAnchors";

/**
 * Which controls a team row exposes:
 * - `match`: the raw name + a follow-team button (works even on an unmatched
 *   name — following an unmatched name follows it BY NAME, creating nothing) +
 *   the link-to-an-org-team combobox. No per-player roster expander — that lives
 *   on the Follow Players step. Used by the wizard's Match Teams step.
 * - `follow`: the raw name + the follow-team button + the roster of per-player
 *   follow toggles, no link combobox. Scoped to the teams you follow (or have a
 *   followed player on). Used by the wizard's Follow Players step.
 * - `both`: the full row (link + follow + roster). The default, so the standalone
 *   event view keeps its combined behavior.
 */
export type TeamPanelMode = 'match' | 'follow' | 'both';

/**
 * Match teams the importer picked up to EXISTING org teams. Assigning a team
 * re-points every game naming it, so that team's roster / starred prospects flow
 * into the itinerary. Typing uses a shared datalist of org team names; a big
 * multi-team event is filterable and row-capped.
 *
 * `mode` splits the panel for the wizard's two team steps (match vs follow); it
 * defaults to `both` so the standalone event view is unaffected.
 */
export function TeamAssociationPanel({
  calendarEventId,
  games,
  onAssociated,
  mode = 'both',
  tourAnchorId,
}: {
  calendarEventId: string;
  games: TournamentGameDto[];
  onAssociated: () => void;
  mode?: TeamPanelMode;
  /**
   * The tour anchor this panel carries. The same panel is the Match Teams list and the Follow
   * Players list, so the step says which one it is.
   */
  tourAnchorId?: TourAnchorId;
}) {
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [filter, setFilter] = useState("");
  const [busyName, setBusyName] = useState<string | null>(null);
  const [error, setError] = useState("");
  // Rosters for every linked team, so the search can match player names (not just team names).
  // Loaded once per team and handed down to each row so the roster isn't fetched twice.
  const [rostersByTeamId, setRostersByTeamId] = useState<Map<string, ProspectResponse[]>>(
    new Map(),
  );
  // Teams the current user follows (per-user). Following a team surfaces its games — present and any
  // added later (bracket advancement / re-import) — in the itinerary, without touching player follows.
  const [followedTeamIds, setFollowedTeamIds] = useState<ReadonlySet<string>>(new Set());
  const [followBusy, setFollowBusy] = useState<ReadonlySet<string>>(new Set());
  // Normalized raw names the user follows by name (no org team). Keyed via normalizeTeamName.
  const [followedTeamNames, setFollowedTeamNames] = useState<ReadonlySet<string>>(new Set());
  // Raw names with a name-follow toggle in flight (unmatched rows have no teamId to key on).
  const [nameFollowBusy, setNameFollowBusy] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let active = true;
    getFollowedTeams()
      .then((ids) => {
        if (active) setFollowedTeamIds(new Set(ids));
      })
      .catch(() => {
        /* non-fatal: follow state just starts empty */
      });
    getFollowedTeamNames()
      .then((names) => {
        if (active) setFollowedTeamNames(new Set(names.map(normalizeTeamName)));
      })
      .catch(() => {
        /* non-fatal: name-follow state just starts empty */
      });
    return () => {
      active = false;
    };
  }, []);

  const toggleFollowTeam = async (teamId: string) => {
    if (followBusy.has(teamId)) return;
    const nextFollowed = !followedTeamIds.has(teamId);
    setFollowBusy((s) => new Set(s).add(teamId));
    setFollowedTeamIds((s) => {
      const n = new Set(s);
      if (nextFollowed) n.add(teamId);
      else n.delete(teamId);
      return n;
    });
    try {
      if (nextFollowed) await followTeam(teamId);
      else await unfollowTeam(teamId);
    } catch {
      setFollowedTeamIds((s) => {
        const n = new Set(s);
        if (nextFollowed) n.delete(teamId);
        else n.add(teamId);
        return n;
      });
    } finally {
      setFollowBusy((s) => {
        const n = new Set(s);
        n.delete(teamId);
        return n;
      });
    }
  };

  // Follow a team straight from its imported row — including one that isn't
  // linked yet. A linked row toggles the id-based follow. An UNMATCHED raw name
  // is followed BY NAME: nothing is created or linked — the follow is stored by
  // name and its games surface in the itinerary via name match. Optimistic,
  // reconciled on error.
  const followByNameRow = async (rawName: string) => {
    const key = normalizeTeamName(rawName);
    if (nameFollowBusy.has(key)) return;
    const nextFollowed = !followedTeamNames.has(key);
    setNameFollowBusy((s) => new Set(s).add(key));
    setFollowedTeamNames((s) => {
      const n = new Set(s);
      if (nextFollowed) n.add(key);
      else n.delete(key);
      return n;
    });
    try {
      if (nextFollowed) await followTeamByName(rawName);
      else await unfollowTeamByName(rawName);
    } catch {
      setFollowedTeamNames((s) => {
        const n = new Set(s);
        if (nextFollowed) n.delete(key);
        else n.add(key);
        return n;
      });
    } finally {
      setNameFollowBusy((s) => {
        const n = new Set(s);
        n.delete(key);
        return n;
      });
    }
  };

  const followRow = (team: TournamentTeamRef) =>
    team.teamId ? toggleFollowTeam(team.teamId) : followByNameRow(team.rawName);

  useEffect(() => {
    let active = true;
    getTeams()
      .then((t) => {
        if (active) setTeams(t);
      })
      .catch(() => {
        // Non-fatal: without the team list the coach just can't assign here.
        if (active) setError("We couldn't load your teams to match against.");
      });
    return () => {
      active = false;
    };
  }, []);

  const teamsById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  const distinct = useMemo(() => distinctTournamentTeams(games), [games]);

  const linkedTeamIds = useMemo(
    () =>
      Array.from(
        new Set(distinct.map((t) => t.teamId).filter((id): id is string => !!id)),
      ),
    [distinct],
  );
  const linkedKey = linkedTeamIds.join(",");
  useEffect(() => {
    let active = true;
    for (const teamId of linkedTeamIds) {
      if (rostersByTeamId.has(teamId)) continue;
      getAllProspectsForTeam(teamId)
        .then((players) => {
          if (!active) return;
          setRostersByTeamId((prev) => {
            if (prev.has(teamId)) return prev;
            const next = new Map(prev);
            next.set(teamId, players);
            return next;
          });
        })
        .catch(() => {
          /* non-fatal: player search just won't match this team */
        });
    }
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedKey]);

  // A team counts as "tracked" when you follow the team itself or you've
  // followed (favorited) at least one of its players.
  const teamHasFollowedPlayer = (teamId: string | null) =>
    !!teamId && !!rostersByTeamId.get(teamId)?.some((p) => p.isFavorite);

  // The Follow step is scoped to the teams you're actually tracking — the ones
  // you followed on the Match Teams step, plus any team you've followed a player
  // on — so it shows only what you care about instead of every linked team.
  // Match/both show every imported team so the coach can link + follow the rest.
  const rows =
    mode === 'follow'
      ? distinct.filter((t) =>
          t.teamId
            ? followedTeamIds.has(t.teamId) || teamHasFollowedPlayer(t.teamId)
            : followedTeamNames.has(normalizeTeamName(t.rawName)),
        )
      : distinct;

  // Deferred so the filter box stays responsive while a big event's every row re-renders behind it.
  const deferredFilter = useDeferredValue(filter);
  const needle = deferredFilter.trim().toLowerCase();
  const filtered = needle
    ? rows.filter((t) => {
        if (t.rawName.toLowerCase().includes(needle)) return true;
        const roster = t.teamId ? rostersByTeamId.get(t.teamId) : undefined;
        return !!roster?.some((p) =>
          `${p.firstName} ${p.lastName}`.toLowerCase().includes(needle),
        );
      })
    : rows;
  // Every team, however big the event: coaches need to see the whole field, and a filter is a convenience, not a gate.
  const shown = filtered;

  if (rows.length === 0) {
    return (
      <p
        className="wiz-sub"
        data-testid="teams-empty"
        {...(tourAnchorId ? tourAnchor(tourAnchorId) : {})}
      >
        {mode === 'follow'
          ? 'Follow a team on the Match Teams step to pick its players here.'
          : 'This tournament has no teams to match yet.'}
      </p>
    );
  }

  const showLinkColumn = mode !== 'follow';

  const handleAssign = async (
    rawName: string,
    currentTeamId: string | null,
    team: TeamResponse,
  ) => {
    if (team.id === currentTeamId) return;
    setBusyName(rawName);
    setError("");
    try {
      await associateTournamentTeam(calendarEventId, rawName, team.id);
      onAssociated();
    } catch {
      setError(`We couldn't link "${rawName}" to ${team.name}. Please try again.`);
    } finally {
      setBusyName(null);
    }
  };

  const linkedCount = distinct.filter((t) => t.teamId && teamsById.has(t.teamId)).length;

  const heading =
    mode === 'follow'
      ? `Players · ${rows.length} team${rows.length === 1 ? '' : 's'} you follow`
      : `Your teams · ${linkedCount} of ${distinct.length} linked`;
  const intro =
    mode === 'follow'
      ? 'These are the teams you follow. Expand one to follow individual players, or follow the whole team. Favorites are followed automatically.'
      : mode === 'match'
        ? 'Match each imported team to one of your existing teams, and follow the ones you want to track. Linking pulls a team into your itinerary.'
        : 'Match each imported team to one of your existing teams, then expand it to follow players. Favorites are followed automatically.';

  return (
    <div
      className="wiz-card wiz-teams"
      data-testid="teams-panel"
      {...(tourAnchorId ? tourAnchor(tourAnchorId) : {})}
    >
      <h3 className="wiz-panel__title">{heading}</h3>
      <p className="wiz-sub">{intro}</p>

      <input
        type="search"
        className="wiz-teams__filter"
        placeholder={`Search ${rows.length} teams or players by name...`}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        data-testid="teams-filter"
      />
      {needle && filtered.length === 0 && (
        <p className="wiz-sub" data-testid="teams-filter-empty">
          No teams or players match "{filter.trim()}".
        </p>
      )}

      {error && (
        <p className="wiz-error" role="alert">
          {error}
        </p>
      )}

      <table className="wiz-table wiz-teams__table">
        <thead>
          <tr>
            <th scope="col">Imported team</th>
            {showLinkColumn && <th scope="col">Linked to</th>}
          </tr>
        </thead>
        <tbody>
          {shown.map((t) => (
            <ExpandableTeamRow
              key={t.rawName}
              team={t}
              teams={teams}
              mode={mode}
              linkedName={t.teamId ? teamsById.get(t.teamId)?.name : undefined}
              busy={busyName === t.rawName}
              onAssign={(team) => handleAssign(t.rawName, t.teamId, team)}
              preloadedPlayers={t.teamId ? rostersByTeamId.get(t.teamId) : undefined}
              isFollowed={
                t.teamId
                  ? followedTeamIds.has(t.teamId)
                  : followedTeamNames.has(normalizeTeamName(t.rawName))
              }
              followBusy={
                t.teamId
                  ? followBusy.has(t.teamId)
                  : nameFollowBusy.has(normalizeTeamName(t.rawName))
              }
              onToggleFollow={() => followRow(t)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Accessible team autocomplete: a styled input + filtered dropdown of existing
 * org teams (replaces the browser's native <datalist>, which renders
 * inconsistently). Keyboard: ↑/↓ to move, Enter to pick, Esc to cancel; click or
 * tab-away closes. Picking a team reports it up; typing filters by name.
 */
function TeamCombobox({
  teams,
  value,
  disabled,
  ariaLabel,
  onSelect,
}: {
  teams: TeamResponse[];
  value: string;
  disabled: boolean;
  ariaLabel: string;
  onSelect: (team: TeamResponse) => void;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  // Reflect an externally-changed link (e.g. after a refetch) in the field.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    const base = q ? teams.filter((t) => t.name.toLowerCase().includes(q)) : teams;
    return base;
  }, [teams, q]);

  // Close on an outside click so the dropdown doesn't linger.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const choose = (team: TeamResponse) => {
    onSelect(team);
    setQuery(team.name);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (open && matches[active]) {
        e.preventDefault();
        choose(matches[active]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(value);
    }
  };

  return (
    <div className="wiz-combo" ref={rootRef}>
      <input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matches[active] ? `${listId}-opt-${active}` : undefined}
        aria-label={ariaLabel}
        className="wiz-combo__input"
        value={query}
        disabled={disabled}
        placeholder="Assign a team..."
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul className="wiz-combo__list" id={listId} role="listbox">
          {matches.length === 0 ? (
            <li className="wiz-combo__empty">No teams match</li>
          ) : (
            matches.map((t, i) => (
              <li
                key={t.id}
                id={`${listId}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                className={`wiz-combo__option${i === active ? " wiz-combo__option--active" : ""}`}
                onMouseEnter={() => setActive(i)}
                // mousedown (not click) so the pick registers before the input blurs.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(t);
                }}
              >
                {t.name}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * One imported-team row. Its controls depend on `mode`:
 * - `match`: the raw name + a follow-team button (follows even an unmatched
 *   name, by name — creating nothing) + the link combobox. No caret / roster expander.
 * - `follow`: the raw name + a follow-team button + an expandable roster for
 *   per-player following (no link combobox).
 * - `both`: everything (combobox + follow + roster), for the standalone view.
 */
function ExpandableTeamRow({
  team,
  teams,
  mode,
  linkedName,
  busy,
  onAssign,
  preloadedPlayers,
  isFollowed,
  followBusy,
  onToggleFollow,
}: {
  team: TournamentTeamRef;
  teams: TeamResponse[];
  mode: TeamPanelMode;
  linkedName?: string;
  busy: boolean;
  onAssign: (team: TeamResponse) => void;
  preloadedPlayers?: ProspectResponse[];
  isFollowed: boolean;
  followBusy: boolean;
  /** Present only when the row is linked to an org team (following applies to that team). */
  onToggleFollow?: () => void;
}) {
  // Default to expanded so a matched team's roster is visible without a click —
  // coaches almost always want to follow players right after matching. The caret
  // still collapses it. Unmatched teams (no teamId) render nothing to expand.
  const [expanded, setExpanded] = useState(true);

  const showRoster = mode !== "match";
  // Follow a team on every step once it's linked — including Match Teams, so a
  // coach can link and follow in one pass. Per-player rosters still stay off the
  // match step (they belong on Follow Players).
  const showFollow = true;
  const showLink = mode !== "follow";
  const canExpand = showRoster && !!team.teamId;
  // Match mode collapses to a single "Linked to" cell; the others use one cell.
  const colSpan = showLink ? 2 : 1;

  return (
    <>
      <tr data-testid={`team-row-${team.rawName}`}>
        <td>
          <div className="wiz-team-name">
            {canExpand ? (
              <button
                type="button"
                className="wiz-team-caret"
                aria-expanded={expanded}
                aria-label={`${expanded ? "Hide" : "Show"} ${team.rawName} roster`}
                onClick={() => setExpanded((v) => !v)}
                data-testid={`team-expand-${team.rawName}`}
              >
                {expanded ? "▾" : "▸"}
              </button>
            ) : (
              <span className="wiz-team-caret wiz-team-caret--empty" aria-hidden="true" />
            )}
            {team.rawName}
            {showFollow && onToggleFollow && (
              <button
                type="button"
                className={`wiz-follow-team${isFollowed ? " wiz-follow-team--on" : ""}`}
                onClick={onToggleFollow}
                disabled={followBusy}
                aria-pressed={isFollowed}
                title="Follow this team — its games (now and any added later) show in your itinerary"
                data-testid={`follow-team-${team.rawName}`}
              >
                {followBusy ? "…" : isFollowed ? "✓ Following" : "＋ Follow team"}
              </button>
            )}
          </div>
        </td>
        {showLink && (
          <td>
            <TeamCombobox
              teams={teams}
              value={linkedName ?? ""}
              disabled={busy}
              ariaLabel={`Link ${team.rawName} to an existing team`}
              onSelect={onAssign}
            />
          </td>
        )}
      </tr>
      {showRoster && expanded && team.teamId && (
        <tr className="wiz-team-roster-row">
          <td colSpan={colSpan}>
            <TeamRoster teamId={team.teamId} preloaded={preloadedPlayers} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The linked team's roster, loaded on expand. Each player has an inline follow
 * toggle backed by the favorite flag (following == favoriting), so a coach can
 * pick exactly who to track without leaving the wizard. Favorited players are
 * already followed. Toggles are optimistic and reconcile on error.
 */
function TeamRoster({
  teamId,
  preloaded,
}: {
  teamId: string;
  preloaded?: ProspectResponse[];
}) {
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; players: ProspectResponse[] }
  >(preloaded ? { status: "ready", players: preloaded } : { status: "loading" });
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    // The parent already loads linked-team rosters (for search); reuse that instead of refetching.
    if (preloaded) {
      setState({ status: "ready", players: preloaded });
      return;
    }
    let active = true;
    setState({ status: "loading" });
    getAllProspectsForTeam(teamId)
      .then((players) => {
        if (active) setState({ status: "ready", players });
      })
      .catch(() => {
        if (active) setState({ status: "error" });
      });
    return () => {
      active = false;
    };
  }, [teamId, preloaded]);

  const setFollowing = (prospectId: string, isFavorite: boolean) => {
    setState((s) =>
      s.status === "ready"
        ? {
            status: "ready",
            players: s.players.map((p) =>
              p.id === prospectId ? { ...p, isFavorite } : p,
            ),
          }
        : s,
    );
  };

  const onToggle = async (player: ProspectResponse) => {
    const next = !player.isFavorite;
    setFollowing(player.id, next); // optimistic
    setPending((s) => new Set(s).add(player.id));
    try {
      await toggleFavorite(player.id, player.isFavorite);
    } catch {
      setFollowing(player.id, player.isFavorite); // revert
    } finally {
      setPending((s) => {
        const n = new Set(s);
        n.delete(player.id);
        return n;
      });
    }
  };


  if (state.status === "loading") {
    return (
      <div className="wiz-players-state">
        <span className="wiz-spinner" aria-hidden="true" />
        <span>Loading roster...</span>
      </div>
    );
  }
  if (state.status === "error") {
    return <p className="wiz-sub">We couldn't load this team's roster.</p>;
  }
  if (state.players.length === 0) {
    return <p className="wiz-sub">No players on this team yet.</p>;
  }

  return (
    <ul className="wiz-roster">
      {state.players.map((p) => {
        const meta = [p.position, p.gradYear].filter(Boolean).join(" · ");
        return (
          <li key={p.id} className="wiz-roster__row">
            <label className="wiz-roster__player">
              <input
                type="checkbox"
                checked={p.isFavorite}
                disabled={pending.has(p.id)}
                onChange={() => onToggle(p)}
                aria-label={`Follow ${p.firstName} ${p.lastName}`}
              />
              <span className="sf-avatar wiz-player-avatar">
                {playerInitials(p.firstName, p.lastName)}
              </span>
              <span className="wiz-roster__name">
                {p.firstName} {p.lastName}
                {meta && <span className="wiz-sub"> · {meta}</span>}
              </span>
            </label>
            {p.isFavorite && <span className="wiz-roster__following">Following</span>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A rough breakdown of what the importer parsed out (games / teams / days /
 * fields, with the date span), shown before team assignment so the coach can
 * sanity-check the import at a glance.
 */
export function ParseSummary({ games }: { games: TournamentGameDto[] }) {
  const summary = useMemo(() => summarizeParse(games), [games]);

  const span =
    summary.firstDay && summary.lastDay
      ? summary.firstDay === summary.lastDay
        ? formatDayHeading(summary.firstDay)
        : `${formatDayHeading(summary.firstDay)} – ${formatDayHeading(summary.lastDay)}`
      : null;

  return (
    <div
      className="wiz-card wiz-parse"
      data-testid="teams-parse-summary"
      {...tourAnchor('build-itinerary.match.summary')}
    >
      <h3 className="wiz-panel__title">Here's what we parsed from the schedule</h3>
      <div className="wiz-parse__stats">
        <div className="wiz-parse__stat">
          <b>{summary.games}</b>
          <span>Games</span>
        </div>
        <div className="wiz-parse__stat">
          <b>{summary.teams}</b>
          <span>Teams</span>
        </div>
        <div className="wiz-parse__stat">
          <b>{summary.days}</b>
          <span>Days</span>
        </div>
        <div className="wiz-parse__stat">
          <b>{summary.fields}</b>
          <span>Fields</span>
        </div>
      </div>
      {span && <p className="wiz-sub" data-testid="teams-parse-span">{span}</p>}
    </div>
  );
}
